import { Link, useRouteError, isRouteErrorResponse } from "react-router-dom";
import { AlertTriangle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useSEO } from "@/lib/use-seo";

/**
 * Route-level error boundary. Without this, a thrown render error leaves the
 * user on a blank white page with no way forward.
 */
const RouteError = () => {
  const error = useRouteError();

  useSEO({
    title: "Something interrupted this page",
    description: "This page failed to load. Reload or head back to the homepage.",
    path: "/",
    noindex: true,
  });

  const detail = isRouteErrorResponse(error)
    ? `${error.status} ${error.statusText}`
    : error instanceof Error
      ? error.message
      : null;

  return (
    <div className="min-h-[70vh] flex items-center bg-background pt-28 pb-16">
      <div className="section-inner max-w-xl text-center">
        <AlertTriangle
          className="w-12 h-12 text-secondary mx-auto mb-6"
          aria-hidden="true"
        />
        <h1 className="text-3xl md:text-4xl font-bold mb-4">
          This page didn&apos;t finish loading
        </h1>
        <p className="text-base text-foreground/80 mb-2 leading-relaxed">
          Reloading usually fixes it. If it keeps happening, email{" "}
          <a
            className="font-semibold text-primary underline underline-offset-4 hover:text-primary/80 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background rounded-sm"
            href="mailto:hello@aiforhrmastermind.com"
          >
            hello@aiforhrmastermind.com
          </a>{" "}
          and tell us which page you were on.
        </p>
        {detail && (
          <p className="text-sm text-muted-foreground mb-8 font-mono break-words">
            {detail}
          </p>
        )}
        <div className="flex flex-wrap gap-3 justify-center mt-6">
          <Button onClick={() => window.location.reload()} size="lg">
            Reload this page
          </Button>
          <Button asChild variant="outline" size="lg">
            <Link to="/">Back to home</Link>
          </Button>
        </div>
      </div>
    </div>
  );
};

export default RouteError;
